// Per-tab recovery, namespaced by authenticated user and record. Only editable
// business fields belong here; never tokens, passwords, attachments or API rows.
export function createDraftBuffer(key, storage = window.sessionStorage) {
  const owner = `${Date.now()}:${Math.random()}`;
  let sequence = 0;
  let remote = {};
  let edits = {};
  let recovery = null;
  let storageError = false;
  const listeners = new Set();
  const notify = () => listeners.forEach(listener => listener());
  try {
    const saved = JSON.parse(storage.getItem(key) || 'null');
    if (saved?.version === 1 && saved.values && typeof saved.values === 'object' && !Array.isArray(saved.values)) recovery = saved.values;
  } catch { storageError = true; }
  function persist({ acknowledgement = false } = {}) {
    try {
      // A previous, unmounted editor may finish after a new editor has already
      // written into the same recovery key. Its late response must not erase it.
      if (acknowledgement) {
        const stored = JSON.parse(storage.getItem(key) || 'null');
        if (stored && stored.owner !== owner) { notify(); return; }
      }
      const values = { ...(recovery || {}), ...Object.fromEntries(Object.entries(edits).map(([name, entry]) => [name, entry.value])) };
      if (Object.keys(values).length) storage.setItem(key, JSON.stringify({ version: 1, owner, values }));
      else storage.removeItem(key);
      storageError = false;
    } catch { storageError = true; }
    notify();
  }
  const buffer = {
    active: true,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    get values() { return { ...remote, ...Object.fromEntries(Object.entries(edits).map(([name, entry]) => [name, entry.value])) }; },
    get count() { return Object.keys(edits).length; },
    get needsSync() { return Object.values(edits).some(entry => entry.synced !== entry.revision); },
    get recovery() { return recovery; },
    get storageError() { return storageError; },
    hydrate(values) { remote = values; notify(); },
    edit(name, value) {
      edits[name] = { value, revision: ++sequence, synced: 0 };
      persist();
    },
    restore() {
      Object.entries(recovery || {}).forEach(([name, value]) => { edits[name] = { value, revision: ++sequence, synced: 0 }; });
      recovery = null;
      persist();
    },
    dismissRecovery() { recovery = null; persist(); },
    acceptServer(name, value) {
      delete edits[name];
      remote[name] = value;
      persist();
    },
    snapshot({ unsyncedOnly = false } = {}) {
      return Object.fromEntries(Object.entries(edits).filter(([, entry]) => !unsyncedOnly || entry.synced !== entry.revision)
        .map(([name, entry]) => [name, { ...entry }]));
    },
    // Acknowledged server revisions advance the base of edits typed while a
    // request was in flight, without replacing their newer business values.
    rebaseAnswerRevisions(snapshot, revisions = {}) {
      Object.entries(revisions).forEach(([name, revision]) => {
        if (!snapshot[name]) return;
        const patch = { answer_revision: revision, expected_answer_revision: revision };
        if (edits[name]) edits[name].value = { ...edits[name].value, ...patch };
        if (remote[name]) remote[name] = { ...remote[name], ...patch };
      });
      persist({ acknowledgement: true });
    },
    acknowledge(snapshot, { committed = false, values = {} } = {}) {
      Object.entries(snapshot).forEach(([name, saved]) => {
        // An acknowledgement can only consume the exact revision it sent.
        if (edits[name]?.revision !== saved.revision) return;
        if (committed) {
          remote[name] = Object.prototype.hasOwnProperty.call(values, name) ? values[name] : saved.value;
          delete edits[name];
        } else edits[name].synced = saved.revision;
      });
      persist({ acknowledgement: true });
    },
    discard() { edits = {}; recovery = null; persist(); },
  };
  return buffer;
}

export function snapshotValues(snapshot) {
  return Object.fromEntries(Object.entries(snapshot).map(([key, entry]) => [key, entry.value]));
}

// A rejected request must not poison the queue. New saves run after old saves,
// so a slow earlier request cannot overwrite a newer revision on the server.
export function createSaveQueue() {
  let tail = Promise.resolve();
  return task => {
    const result = tail.then(task);
    tail = result.catch(() => {});
    return result;
  };
}
